"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  Background,
  Controls,
  type Node,
  type Edge,
  type OnConnect,
  type EdgeMouseHandler,
  type NodeMouseHandler,
  type Connection,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";

import { useTranslations } from "next-intl";
import type { Source, Target, RouteItem } from "@/lib/types/routing";
import { SourceNode, type SourceNodeData } from "./source-node";
import { TargetNode, type TargetNodeData } from "./target-node";
import {
  RouteSidePanel,
  type PanelState,
} from "./route-side-panel";

// Register custom node types (must be stable references, defined outside component)
const nodeTypes = {
  sourceNode: SourceNode,
  targetNode: TargetNode,
};

interface FlowCanvasProps {
  sources: Source[];
  targets: Target[];
  initialRoutes: RouteItem[];
}

function buildNodes(
  sources: Source[],
  targets: Target[],
  onSourceClick: (s: Source) => void,
  onTargetClick: (t: Target) => void
): Node[] {
  const sourceNodes: Node[] = sources.map((source, i) => ({
    id: `source-${source.id}`,
    type: "sourceNode",
    position: { x: 40, y: i * 130 + 20 },
    data: { source, onNodeClick: onSourceClick } satisfies SourceNodeData,
  }));

  const targetNodes: Node[] = targets.map((target, i) => ({
    id: `target-${target.id}`,
    type: "targetNode",
    position: { x: 480, y: i * 130 + 20 },
    data: { target, onNodeClick: onTargetClick } satisfies TargetNodeData,
  }));

  return [...sourceNodes, ...targetNodes];
}

function buildEdges(routes: RouteItem[]): Edge[] {
  const edges: Edge[] = [];
  for (const route of routes) {
    for (const targetId of route.targetIds) {
      edges.push({
        id: `route-${route.id}-${targetId}`,
        source: `source-${route.sourceId}`,
        target: `target-${targetId}`,
        sourceHandle: "out",
        targetHandle: "in",
        data: {
          routeId: route.id,
          routeName: route.name,
          stepCount: route.steps?.length ?? 0,
        },
        label:
          (route.steps?.length ?? 0) > 0
            ? `${route.name} (${route.steps.length} rule${route.steps.length !== 1 ? "s" : ""})`
            : route.name,
        style: { stroke: "var(--primary)", strokeWidth: 1.5 },
        labelStyle: { fontSize: 11, fill: "var(--muted-foreground)" },
        labelBgStyle: {
          fill: "var(--background)",
          fillOpacity: 0.85,
        },
        labelBgPadding: [6, 4] as [number, number],
      });
    }
  }
  return edges;
}

function FlowInner({ sources, targets, initialRoutes }: FlowCanvasProps) {
  const t = useTranslations();
  const [routes, setRoutes] = useState<RouteItem[]>(initialRoutes);

  // Node-click popover (lightweight inline tooltip)
  const [nodePopover, setNodePopover] = useState<{
    kind: "source" | "target";
    entity: Source | Target;
  } | null>(null);

  const handleSourceClick = useCallback((source: Source) => {
    setNodePopover({ kind: "source", entity: source });
  }, []);

  const handleTargetClick = useCallback((target: Target) => {
    setNodePopover({ kind: "target", entity: target });
  }, []);

  const initialNodes = useMemo(
    () => buildNodes(sources, targets, handleSourceClick, handleTargetClick),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [] // intentionally build only on mount; positions are mutable
  );

  const initialEdges = useMemo(() => buildEdges(routes), [routes]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Patch target node data when real-time TargetStatusChanged events update the targets prop
  useEffect(() => {
    setNodes((prev) =>
      prev.map((node) => {
        if (node.type !== "targetNode") return node;
        const nodeData = node.data as unknown as import("./target-node").TargetNodeData;
        const updated = targets.find((t) => t.id === nodeData.target.id);
        if (!updated) return node;
        return {
          ...node,
          data: { ...nodeData, target: updated } as unknown as typeof node.data,
        };
      })
    );
  }, [targets, setNodes]);

  // Side panel state
  const [panelOpen, setPanelOpen] = useState(false);
  const [panelState, setPanelState] = useState<PanelState | null>(null);

  // When user drags a new connection
  const onConnect: OnConnect = useCallback(
    (connection: Connection) => {
      const rawSourceId = connection.source?.replace("source-", "") ?? "";
      const rawTargetId = connection.target?.replace("target-", "") ?? "";
      const targetName =
        targets.find((t) => t.id === rawTargetId)?.name ?? "Destination";

      setPanelState({
        mode: "create",
        sourceId: rawSourceId,
        targetIds: [rawTargetId],
        routeName: `Flow → ${targetName}`,
        steps: [],
      });
      setPanelOpen(true);

      // Temporarily add a ghost edge so the user sees the connection
      setEdges((eds) =>
        addEdge(
          {
            ...connection,
            id: `ghost-${Date.now()}`,
            style: { stroke: "var(--primary)", strokeDasharray: "4 3" },
          },
          eds
        )
      );
    },
    [targets, setEdges]
  );

  // When user clicks an existing edge → open in edit mode
  const onEdgeClick: EdgeMouseHandler = useCallback(
    (_evt, edge) => {
      const routeId = edge.data?.routeId as string | undefined;
      if (!routeId) return;
      const route = routes.find((r) => r.id === routeId);
      if (!route) return;

      setPanelState({
        mode: "edit",
        routeId: route.id,
        sourceId: route.sourceId,
        targetIds: route.targetIds,
        routeName: route.name,
        steps: route.steps,
      });
      setPanelOpen(true);
    },
    [routes]
  );

  // Node click, we already handle inside each node component via data callback.
  // This handler prevents ReactFlow from deselecting nodes when clicking.
  const onNodeClick: NodeMouseHandler = useCallback(() => {
    // handled inside custom nodes via data.onNodeClick
  }, []);

  // After save: rebuild edges from updated route list
  const handleSaved = useCallback(
    (savedRoute: RouteItem) => {
      setRoutes((prev) => {
        const existing = prev.findIndex((r) => r.id === savedRoute.id);
        if (existing >= 0) {
          const next = [...prev];
          next[existing] = savedRoute;
          return next;
        }
        return [...prev, savedRoute];
      });
      // Remove any ghost edges, then add real ones
      setEdges((eds) => {
        const cleaned = eds.filter(
          (e) =>
            !e.id.startsWith("ghost-") &&
            !(e.data?.routeId === savedRoute.id)
        );
        const newEdges = savedRoute.targetIds.map((tid) => ({
          id: `route-${savedRoute.id}-${tid}`,
          source: `source-${savedRoute.sourceId}`,
          target: `target-${tid}`,
          sourceHandle: "out",
          targetHandle: "in",
          data: {
            routeId: savedRoute.id,
            routeName: savedRoute.name,
            stepCount: savedRoute.steps?.length ?? 0,
          },
          label:
            (savedRoute.steps?.length ?? 0) > 0
              ? `${savedRoute.name} (${savedRoute.steps.length} rule${savedRoute.steps.length !== 1 ? "s" : ""})`
              : savedRoute.name,
          style: { stroke: "var(--primary)", strokeWidth: 1.5 },
          labelStyle: { fontSize: 11, fill: "var(--muted-foreground)" },
          labelBgStyle: {
            fill: "var(--background)",
            fillOpacity: 0.85,
          },
          labelBgPadding: [6, 4] as [number, number],
        }));
        return [...cleaned, ...newEdges];
      });
    },
    [setEdges]
  );

  const handleDeleted = useCallback(
    (routeId: string) => {
      setRoutes((prev) => prev.filter((r) => r.id !== routeId));
      setEdges((eds) =>
        eds.filter((e) => e.data?.routeId !== routeId)
      );
    },
    [setEdges]
  );

  const handleClosePanel = useCallback(() => {
    setPanelOpen(false);
    // Remove ghost edges if user cancelled
    setEdges((eds) => eds.filter((e) => !e.id.startsWith("ghost-")));
  }, [setEdges]);

  return (
    <div className="relative h-full w-full">
      {/* Column headers */}
      <div
        className="pointer-events-none absolute inset-x-0 top-3 z-10 flex px-4"
        aria-hidden="true"
      >
        <span
          className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
          style={{ marginLeft: 40 }}
        >
          {t("dashboard.flow.sourcesLabel")}
        </span>
        <span
          className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground"
          style={{ marginLeft: "auto", marginRight: 40 }}
        >
          {t("dashboard.flow.targetsLabel")}
        </span>
      </div>

      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onEdgeClick={onEdgeClick}
        onNodeClick={onNodeClick}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.3}
        maxZoom={2}
        proOptions={{ hideAttribution: true }}
        className="bg-background"
      >
        <Background color="var(--border)" gap={20} size={1} />
        <Controls
          showInteractive={false}
          className="signalync-flow-controls"
        />
      </ReactFlow>

      {/* Node popover */}
      {nodePopover && (
        <div className="fixed inset-0 z-40" onClick={() => setNodePopover(null)}>
          <div
            className="absolute end-4 top-16 min-w-[200px] rounded-xl border border-border bg-card p-4 shadow-md backdrop-blur"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.14em] text-muted-foreground">
              {nodePopover.kind === "source"
                ? t("dashboard.flow.sourcesLabel")
                : t("dashboard.flow.targetsLabel")}
            </p>
            <p className="mt-1 text-sm font-medium">
              {nodePopover.entity.name}
            </p>
            <a
              href={
                nodePopover.kind === "source"
                  ? `/sources/${nodePopover.entity.id}`
                  : `/destinations/${nodePopover.entity.id}`
              }
              className="mt-3 inline-block text-xs text-primary underline-offset-2 hover:underline"
              onClick={() => setNodePopover(null)}
            >
              {t("dashboard.flow.viewDetails")}
            </a>
          </div>
        </div>
      )}

      {/* Route side panel */}
      <RouteSidePanel
        open={panelOpen}
        state={panelState}
        sources={sources}
        targets={targets}
        onClose={handleClosePanel}
        onSaved={handleSaved}
        onDeleted={handleDeleted}
      />
    </div>
  );
}

export function FlowCanvas(props: FlowCanvasProps) {
  // LTR island: ReactFlow's source -> target flow direction is conventionally
  // LTR even on RTL dashboards. Per spec Section 5 (LTR islands).
  return (
    <div className="h-full w-full" dir="ltr">
      <ReactFlowProvider>
        <FlowInner {...props} />
      </ReactFlowProvider>
    </div>
  );
}
